import {ReactElement, useContext, useState} from "react";
import {
    RevenueModelType,
    useAssociateUsersWithAcquisitionPropertyMutation,
    useCreateAcquisitionPropertyMutation,
    VersionType,
} from "../../__generated__/generated_types";

import {Close, Modal} from "@zendeskgarden/react-modals";
import {toast} from "react-toastify";
import AcquisitionPropertyForm, {TAcquisitionPropertyRecord} from "./components/acquisition-property-form/AcquisitionPropertyForm";
import {AuthContext} from "../../contexts/AuthContext";

export interface ICreateAcquisitionProperty {
    formVisible: boolean,
    onModalClose: () => void,
    onCreate: () => void,
}

export function AcquisitionPropertyCreateModal(props: ICreateAcquisitionProperty): ReactElement {

    const {budgetYear} = useContext(AuthContext);

    const {formVisible, onModalClose, onCreate} = props;

    const [saveUsersForProperty] = useAssociateUsersWithAcquisitionPropertyMutation({
        notifyOnNetworkStatusChange: true,
    });
    const [saveProperty, {loading: createPropertyLoading}] = useCreateAcquisitionPropertyMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted: data => {
            if (data) {
                toast.success(`${data.createAcquisitionProperty.name} created`);
                saveUsersForProperty({
                    variables: {
                        propertyId: data.createAcquisitionProperty.id,
                        userIds: usersForProperty,
                    }
                });
                onCreate();
            }
            reset();
        },
    });

    const [property, setProperty] = useState<TAcquisitionPropertyRecord>({
        name: "",
        isDeactivated: false,
        acquisitionBudgetStartMonth: 0,
        acquisitionBudgetYear: budgetYear,
        acquisitionBudgetPeriod: VersionType.Reforecast,
        reforecastYear: budgetYear - 1,
        revenueConfiguration: RevenueModelType.Original,
        originalRevenueModelSubsidyEnabled: false
    });
    const [usersForProperty, setUsersForProperty] = useState<string[]>([]);

    function updateProperty(update: Partial<TAcquisitionPropertyRecord>): void {
        if (property) {
            setProperty({
                ...property,
                ...update,
            });
        }
    }

    function doSaveProperty(): void {
        if (property) {
            saveProperty({
                variables: {
                    name: property.name,
                    isDeactivated: property.isDeactivated,
                    acquisitionBudgetStartMonth: property.acquisitionBudgetStartMonth,
                    acquisitionBudgetYear: property.acquisitionBudgetYear,
                    acquisitionBudgetPeriod: property.acquisitionBudgetPeriod,
                    reforecastYear: property.reforecastYear,
                    revenueConfiguration: property.revenueConfiguration,
                    originalRevenueModelSubsidyEnabled: property.originalRevenueModelSubsidyEnabled
                }
            }).catch(() => { /* Do Nothing */ });
        }
    }

    function reset(): void {
        setProperty({
            name: "",
            isDeactivated: false,
            acquisitionBudgetStartMonth: 0,
            acquisitionBudgetYear: budgetYear,
            acquisitionBudgetPeriod: VersionType.Reforecast,
            reforecastYear: budgetYear - 1,
            revenueConfiguration: RevenueModelType.Original,
            originalRevenueModelSubsidyEnabled: false
        });
        setUsersForProperty([]);
        onModalClose();
    }

    if (!formVisible) {
        return <></>;
    }

    return (
        <Modal onClose={reset}>
            <AcquisitionPropertyForm
                isCreate
                property={property}
                usersForProperty={usersForProperty}
                loading={createPropertyLoading}
                onChange={updateProperty}
                onUsersChange={setUsersForProperty}
                onSubmit={doSaveProperty}
                onClose={reset}
            />
            <Close aria-label="Close modal" />
        </Modal>
    );
}